import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Landmark, Sparkles, TrendingUp, ShieldCheck, Clock, ArrowRight, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BENEFITS = [
  { icon: Landmark, title: "Grant & funding help", body: "We surface grants and funding programs matched to your business, industry and location — and show you how to go after them." },
  { icon: FileText, title: "Lender-ready format", body: "The exact sections banks, the SBA and investors expect — executive summary, market analysis, financials and funding request." },
  { icon: Sparkles, title: "Built with AI in minutes", body: "Answer a few guided questions and our AI drafts each section for you. Edit anything, keep what fits your voice." },
  { icon: TrendingUp, title: "Investor-ready financials", body: "Projections and a funding request laid out clearly, so lenders and partners can say yes faster." },
];

const STEPS = [
  ["1", "Tell us about your business", "A short guided Q&A captures your idea, market and goals — no blank-page overwhelm."],
  ["2", "AI drafts your plan", "Each section is written for you in a lender-ready format. Tweak the wording anytime."],
  ["3", "See your grant & funding matches", "We point you to grants and funding programs that fit your business."],
  ["4", "Unlock your PDF for $99", "Building and editing is free. Pay once to download the polished, lender-ready PDF."],
];

const INCLUDED = [
  "Executive summary", "Company & product overview", "Market & competitor analysis",
  "Marketing & growth strategy", "Operations & team plan", "3-year financial projections",
  "Funding request & use of funds", "AI-matched grant & funding leads",
];

const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";

export default function BusinessPlanLanding() {
  const navigate = useNavigate();
  const { user, setSession } = useAuth();
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const start = async (e) => {
    e.preventDefault();
    if (user) { navigate("/portal?view=plan"); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/business-plan/start", { name: form.name.trim(), email: form.email.trim() });
      if (data.existing) {
        toast.info("You already have an account — please sign in to continue your plan.");
        navigate("/login");
        return;
      }
      setSession(data.token, data.user);
      toast.success("Your workspace is ready — let's build your plan.");
      navigate("/portal?view=plan");
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="business-plan-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <span className="h-px w-8 bg-gold" /> Business Plan
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">
              A lender-ready business plan — <span className="text-gold">with grant &amp; funding help.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Answer a few questions and our AI writes your plan in the format banks, the SBA and investors expect — then shows you the grants and funding programs your business may qualify for. Start free, unlock the PDF when you're ready.
            </p>
            <p className="text-sm text-gray-400 mt-4 max-w-2xl flex items-start gap-2" data-testid="plan-vs-assessment-note">
              <Sparkles size={15} className="text-gold shrink-0 mt-0.5" />
              <span>Already did the free <a href="/start" className="text-gold hover:underline">Business Assessment</a>? That gave you your direction and profile. This turns it into the <span className="text-white">official, lender-ready document</span> — built from what you already told us, so there's nothing to re-enter.</span>
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#get-started" data-testid="plan-hero-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">
                Start my free business plan <ArrowRight size={16} />
              </a>
              <span className="text-sm text-gray-400">Build it <span className="text-white font-semibold">free</span> · <span className="text-white font-semibold">$99</span> to unlock your PDF</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="border border-white/10 bg-navy-800 p-6" data-testid={`plan-benefit-${i}`}>
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold mb-4"><b.icon size={20} /></div>
              <h3 className="font-heading text-lg text-white mb-2">{b.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <h2 className="font-heading text-3xl text-white mb-3">How it works</h2>
          <p className="text-gray-400 mb-10 max-w-2xl">No blank page, no jargon. We guide you the whole way.</p>
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="relative">
                <div className="text-gold font-heading text-4xl mb-3">{n}</div>
                <h3 className="font-heading text-lg text-white mb-2">{t}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Included + Pricing + Start form */}
      <section id="get-started" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* What's included */}
          <div>
            <h2 className="font-heading text-3xl text-white mb-4">What's in your plan</h2>
            <p className="text-gray-400 mb-6 max-w-lg">Everything a lender or grant reviewer looks for — plus funding leads matched to your business.</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {INCLUDED.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-gray-400">
              <span className="flex items-center gap-2"><Clock size={16} className="text-gold" /> Ready in minutes</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-gold" /> Yours to keep</span>
            </div>
          </div>

          {/* Pricing + form */}
          <div>
            <div className="border-2 border-gold bg-gold/10 p-6 mb-4 relative">
              <span className="absolute -top-3 left-6 bg-gold text-navy text-[11px] font-bold uppercase tracking-wider px-2 py-0.5">Fair, founder-friendly pricing</span>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-heading text-gold">$99</span>
                <span className="text-gray-400 text-sm">one-time — unlock your PDF</span>
              </div>
              <p className="text-gray-200 text-sm mt-2">Build and edit your entire plan for <strong>free</strong>. Only pay when you want to download the polished, lender-ready PDF. No subscription.</p>
            </div>

            <form onSubmit={start} className="border border-white/10 bg-navy-800 p-6 space-y-4" data-testid="plan-start-form">
              <h3 className="font-heading text-xl text-white">Start your free business plan</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="plan-name" className={INPUT} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="plan-email" type="email" className={INPUT} placeholder="you@business.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <button type="submit" disabled={busy} data-testid="plan-start-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <>Build my plan free <ArrowRight size={16} /></>}
              </button>
              <p className="text-[11px] text-gray-500 text-center">We'll set up your private workspace instantly. No card needed to build.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
